import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSocieties, adminDeleteSociety, adminExportSocietyOnboard, SocietySummary, updateSociety } from '../services/api';
import { downloadOnboardExcel, onboardExportFileName } from '../utils/onboardExcel';
import './AdminSocieties.css';

const AdminSocieties: React.FC = () => {
  const [societies, setSocieties] = useState<SocietySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Inline edit
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [exportingId, setExportingId] = useState<number | null>(null);
  const [exportError, setExportError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getSocieties();
      setSocieties(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load societies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    setDeleting(true);
    setDeleteError('');
    try {
      await adminDeleteSociety(id);
      setSocieties(prev => prev.filter(s => s.id !== id));
      setConfirmDeleteId(null);
    } catch (e: any) {
      setDeleteError(e.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const startEdit = (s: SocietySummary) => {
    setEditId(s.id);
    setEditName(s.name);
    setEditCity(s.city ?? '');
    setEditLocation(s.location ?? '');
    setSaveError('');
  };

  const handleSave = async () => {
    if (!editId) return;
    setSaving(true);
    setSaveError('');
    try {
      await updateSociety(editId, { name: editName, city: editCity, location: editLocation });
      setSocieties(prev => prev.map(s => s.id === editId ? { ...s, name: editName, city: editCity, location: editLocation } : s));
      setEditId(null);
    } catch (e: any) {
      setSaveError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (s: SocietySummary) => {
    setExportError('');
    setExportingId(s.id);
    try {
      const res = await adminExportSocietyOnboard(s.id);
      const fileName = onboardExportFileName(s.name, s.id);
      downloadOnboardExcel(
        {
          society: res.data.society,
          blocks: res.data.blocks,
          flats: res.data.flats,
        },
        fileName,
      );
    } catch (e: unknown) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportingId(null);
    }
  };

  const filtered = societies.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || (s.city || '').toLowerCase().includes(q);
  });

  return (
    <div className="admin-societies">
      <div className="admin-societies-header">
        <div>
          <h2>Societies</h2>
          <p className="admin-societies-subtitle">
            All registered societies. Click <strong>Edit</strong> to update name/city/location inline,
            or <strong>Onboard New</strong> to create a society from an Excel upload.
            Use <strong>Export</strong> to download society data in the same Excel format as bulk onboard.
          </p>
        </div>
        <div className="admin-societies-header-actions">
          <Link to="/admin/onboard-society" className="admin-soc-btn admin-soc-btn-primary">
            + Onboard New Society
          </Link>
        </div>
      </div>

      {error && <div className="admin-societies-error">{error}</div>}
      {exportError && <div className="admin-societies-error">{exportError}</div>}

      {/* Search */}
      <div className="admin-societies-toolbar">
        <input
          className="admin-soc-search"
          type="text"
          placeholder="Search by name or city…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="admin-soc-btn admin-soc-btn-outline" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : '↺ Refresh'}
        </button>
      </div>

      {loading && <div className="admin-societies-loading">Loading societies…</div>}

      {!loading && (
        <div className="admin-societies-table-wrap">
          <table className="admin-societies-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>City</th>
                <th>Location</th>
                <th>Flats</th>
                <th>Members</th>
                <th>Created</th>
                <th style={{ minWidth: 260 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-societies-empty">
                    {search ? 'No societies match your search.' : 'No societies found.'}
                  </td>
                </tr>
              ) : (
                filtered.map(s => (
                  <tr key={s.id} className={editId === s.id ? 'admin-soc-row-edit' : ''}>
                    <td className="cell-muted">{s.id}</td>
                    {editId === s.id ? (
                      <>
                        <td><input className="admin-soc-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" /></td>
                        <td><input className="admin-soc-input" value={editCity} onChange={e => setEditCity(e.target.value)} placeholder="City" /></td>
                        <td><input className="admin-soc-input" value={editLocation} onChange={e => setEditLocation(e.target.value)} placeholder="Location" /></td>
                        <td>{s.flat_count ?? '—'}</td>
                        <td>{s.member_count ?? '—'}</td>
                        <td className="cell-muted">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                        <td>
                          <div className="admin-soc-actions">
                            <button className="admin-soc-btn admin-soc-btn-primary admin-soc-btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                            <button className="admin-soc-btn admin-soc-btn-outline admin-soc-btn-sm" onClick={() => setEditId(null)} disabled={saving}>Cancel</button>
                          </div>
                          {saveError && <div className="admin-soc-row-error">{saveError}</div>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="cell-strong">{s.name}</td>
                        <td>{s.city ?? '—'}</td>
                        <td className="cell-muted">{s.location ?? '—'}</td>
                        <td>{s.flat_count ?? '—'}</td>
                        <td>{s.member_count ?? '—'}</td>
                        <td className="cell-muted">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                        <td>
                          <div className="admin-soc-actions">
                            <button
                              type="button"
                              className="admin-soc-btn admin-soc-btn-outline admin-soc-btn-sm"
                              onClick={() => void handleExport(s)}
                              disabled={exportingId === s.id}
                            >
                              {exportingId === s.id ? 'Exporting…' : 'Export'}
                            </button>
                            <Link to={`/admin/societies/${s.id}`} className="admin-soc-btn admin-soc-btn-outline admin-soc-btn-sm">
                              View
                            </Link>
                            <button className="admin-soc-btn admin-soc-btn-outline admin-soc-btn-sm" onClick={() => startEdit(s)}>
                              Edit
                            </button>
                            {confirmDeleteId === s.id ? (
                              <>
                                <button className="admin-soc-btn admin-soc-btn-danger admin-soc-btn-sm" onClick={() => handleDelete(s.id)} disabled={deleting}>
                                  {deleting ? '…' : 'Confirm delete'}
                                </button>
                                <button className="admin-soc-btn admin-soc-btn-outline admin-soc-btn-sm" onClick={() => { setConfirmDeleteId(null); setDeleteError(''); }}>
                                  Cancel
                                </button>
                                {deleteError && <span className="admin-soc-row-error">{deleteError}</span>}
                              </>
                            ) : (
                              <button className="admin-soc-btn admin-soc-btn-danger admin-soc-btn-sm" onClick={() => { setConfirmDeleteId(s.id); setDeleteError(''); }}>
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSocieties;
